/**
 * ThisWeekList — 3-task checkbox list for the RETURN hero per spec §3.4.
 *
 * Phase 4c: now consumes the explicit phase → week → task tree from the
 * Phase 4a backend rewrite. Picks the first week that still has incomplete
 * tasks (across all phases, in order), then renders up to 3 tasks from
 * that week. Falls back to last week's tasks once all are done.
 *
 * Checkbox toggles call `toggleRoadmapTask(roadmapId, taskId, completed)`
 * with optimistic React Query cache update + background refetch.
 */

import React from "react";
import {
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  RoadmapPhase,
  RoadmapTaskCompletion,
  toggleRoadmapTask,
} from "../../api/client";

interface ThisWeekListProps {
  roadmapId: string;
  phases: RoadmapPhase[];
  completions: RoadmapTaskCompletion[];
}

interface WeekTask {
  id: string;
  title: string;
  completed: boolean;
}

function pickThisWeek(
  phases: RoadmapPhase[],
  doneIds: Set<string>,
): WeekTask[] {
  // First incomplete week across all phases — flat scan in spec order.
  for (const phase of phases) {
    for (const week of phase.weeks ?? []) {
      const tasks = week.tasks ?? [];
      const incomplete = tasks.filter((t) => !doneIds.has(t.id));
      if (incomplete.length === 0) continue;

      // Always render up to 3 rows: prefer incomplete, backfill with recent done.
      const chosen: WeekTask[] = incomplete.slice(0, 3).map((t) => ({
        id: t.id,
        title: t.title,
        completed: false,
      }));
      if (chosen.length < 3) {
        const filler = tasks
          .filter((t) => doneIds.has(t.id))
          .slice(0, 3 - chosen.length)
          .map((t) => ({ id: t.id, title: t.title, completed: true }));
        chosen.push(...filler);
      }
      return chosen;
    }
  }

  // All weeks complete. Show the last week's last 3 done tasks as a
  // celebratory fallback; a proper 100%-done hero is deferred per spec §10.
  const lastPhase = phases[phases.length - 1];
  const lastWeek = lastPhase?.weeks?.[lastPhase.weeks.length - 1];
  if (!lastWeek) return [];
  return (lastWeek.tasks ?? []).slice(-3).map((t) => ({
    id: t.id,
    title: t.title,
    completed: true,
  }));
}

export default function ThisWeekList({
  roadmapId,
  phases,
  completions,
}: ThisWeekListProps) {
  const { t } = useTranslation("home");
  const queryClient = useQueryClient();

  const doneIds = React.useMemo(
    () => new Set(completions.map((c) => c.task_id)),
    [completions],
  );
  const thisWeek = pickThisWeek(phases, doneIds);

  const toggleMutation = useMutation({
    mutationFn: async (task: WeekTask) =>
      toggleRoadmapTask(roadmapId, task.id, !task.completed),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["roadmap-task-completions", roadmapId],
      });
    },
  });

  return (
    <Stack spacing={1.5}>
      <Typography
        component="span"
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "text.secondary",
        }}
      >
        {t("ThisWeekList.thisWeekLabel")}
      </Typography>
      <Stack spacing={0.5}>
        {thisWeek.map((task) => (
          <FormControlLabel
            key={task.id}
            sx={{
              m: 0,
              alignItems: "flex-start",
              gap: 1,
              py: 0.5,
              borderRadius: 1,
              "&:hover": { bgcolor: "action.hover" },
            }}
            control={
              <Checkbox
                checked={task.completed}
                onChange={() => toggleMutation.mutate(task)}
                disabled={toggleMutation.isPending}
                size="small"
                sx={{
                  p: 0.5,
                  mt: 0.25,
                  color: "text.secondary",
                  "&.Mui-checked": { color: "success.main" },
                }}
              />
            }
            label={
              <Typography
                component="span"
                sx={{
                  fontSize: 13.5,
                  lineHeight: 1.45,
                  color: task.completed ? "text.tertiary" : "text.primary",
                  textDecoration: task.completed ? "line-through" : "none",
                }}
              >
                {task.title}
              </Typography>
            }
          />
        ))}
      </Stack>
    </Stack>
  );
}
